import { ReactElement } from 'react'
import { Radio, RadioProps, Typography, FormControlLabel } from '@mui/material'
import { makeStyles } from 'tss-react/mui'

const useStyles = makeStyles<{ disabled: boolean; size: 'small' | 'medium' }>()(
  (theme, { disabled, size }) => {
    return {
      root: {
        padding: '0',
        marginRight: theme.spacing(2),
        '&:hover': {
          borderRadius: '50%',
          outline: `3px solid ${theme.palette.primary.light}`,
        },
      },
      icon: {
        display: 'flex',
        border: disabled
          ? theme.palette.common.white
          : `1px solid ${theme.palette.grey[500]}`,
        borderRadius: '50%',
        backgroundColor: disabled
          ? theme.palette.grey[50]
          : theme.palette.common.white,
        height: size === 'medium' ? '24px' : '20px',
        width: size === 'medium' ? '24px' : '20px',
      },
      checkedIcon: {
        '&:before': {
          content: '""',
          display: 'block',
          backgroundColor: disabled
            ? theme.palette.grey[800]
            : theme.palette.primary.main,
          borderRadius: '50%',
          position: 'absolute',
          height: '58%',
          width: '58%',
          top: '21%',
          left: '21%',
        },
      },
      customRadio: {
        marginLeft: 0,
        '& .MuiIconButton-label': {
          '& span': {
            height: size === 'medium' ? '24px' : '20px',
            width: size === 'medium' ? '24px' : '20px',
          },
        },
      },
      disabledText: {
        '& .MuiTypography-body1 ': {
          color: theme.palette.grey[800],
        },
      },
    }
  }
)

interface CommonRadioProps {
  name: string
  value: string | number
  label: string | ReactElement
  checked: boolean
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  size?: RadioProps['size']
  disabled?: boolean
  id?: string
  'data-testid'?: string
}

const CommonRadio = ({
  name,
  value,
  label,
  checked,
  onChange,
  size = 'medium',
  disabled = false,
  id,
  'data-testid': dataTestId,
}: CommonRadioProps) => {
  const { cx, classes } = useStyles({ disabled, size })

  const generatedLabel =
    typeof label === 'string' ? (
      <Typography variant="body1">{label}</Typography>
    ) : (
      label
    )

  return (
    <FormControlLabel
      className={cx(classes.customRadio, {
        [classes.disabledText]: disabled,
      })}
      value={value}
      control={
        <Radio
          data-testid={dataTestId}
          id={id}
          className={classes.root}
          name={name}
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          size={size}
          checkedIcon={
            <span className={cx(classes.icon, classes.checkedIcon)} />
          }
          icon={<span className={classes.icon} />}
        />
      }
      label={generatedLabel}
    />
  )
}

export default CommonRadio
